import { chunkKeyInt, TILE_SIZE } from '@dreamgame/shared';
import { Graphics } from 'pixi.js';
import { type ChunkWithObjects, getAllChunks } from '../../state/chunks';
import { CANOPY_COLORS, TREE_OBJECT_TYPES, treeJitterX, treeJitterY } from './forestUtils';
import type { VisibleChunk } from './types';

// ============================================================================
// ForestCanopyLayer - Semi-transparent canopy overlay above pawns/NPCs
// ============================================================================
// Draws semi-transparent colored ellipses at tree canopy positions, rendered
// ABOVE pawns/NPCs but below projectiles/selection. Creates the illusion of
// walking "under" the forest canopy. Follows WaterEffectsLayer caching pattern.
// ============================================================================

let graphics: Graphics;

export const createForestCanopyLayer = (): Graphics => {
  graphics = new Graphics();
  return graphics;
};

// Canopy overlay config
const CANOPY_ALPHA = 0.25;
const CANOPY_RADIUS_X = 38;
const CANOPY_RADIUS_Y = 30;
const CANOPY_DEFAULT_COLOR = 0x3a7a2a;
// Canopy ellipse sits in upper portion of tree (trees are 1x2, canopy center in top tile)
const CANOPY_OFFSET_Y = TILE_SIZE * 0.35;

// PERFORMANCE: Frame-level cache (same pattern as WaterEffectsLayer)
let _cachedChunksRef: ReadonlyMap<number, ChunkWithObjects> | null = null;
let _cachedChunksSize = 0;
let _cachedVisibleHash = 0;

// PERFORMANCE: Preallocated temp for chunkKeyInt
const _chunkKeyTemp = { x: 0, y: 0 };

// Hash visible chunk coordinates for cache invalidation
const computeVisibleChunksHash = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): number => {
  let hash = visibleChunks.length;
  for (let i = 0; i < visibleChunks.length; i++) {
    const vc = visibleChunks[i];
    if (vc) {
      hash = (hash * 31 + vc.x) | 0;
      hash = (hash * 31 + vc.y) | 0;
    }
  }
  return hash;
};

// Draw canopy overlay ellipses for trees in a chunk
const drawChunkCanopy = ({ chunk }: { chunk: ChunkWithObjects }): void => {
  for (const obj of chunk.objects) {
    if (!TREE_OBJECT_TYPES.has(obj.objectType)) {
      continue;
    }

    const jx = treeJitterX({ tileX: obj.tileX, tileY: obj.tileY });
    const jy = treeJitterY({ tileX: obj.tileX, tileY: obj.tileY });

    // Canopy center in upper portion of tree + jitter
    const centerX = obj.tileX * TILE_SIZE + TILE_SIZE / 2 + jx;
    const centerY = obj.tileY * TILE_SIZE + CANOPY_OFFSET_Y + jy;

    const color = CANOPY_COLORS[obj.objectType] ?? CANOPY_DEFAULT_COLOR;

    graphics.setFillStyle({ color, alpha: CANOPY_ALPHA });
    graphics.ellipse(centerX, centerY, CANOPY_RADIUS_X, CANOPY_RADIUS_Y);
    graphics.fill();
  }
};

export const drawForestCanopy = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  const loadedChunks = getAllChunks();

  // PERFORMANCE: Skip when nothing changed
  const visibleHash = computeVisibleChunksHash({ visibleChunks });
  const needsRecompute =
    loadedChunks !== _cachedChunksRef ||
    loadedChunks.size !== _cachedChunksSize ||
    visibleHash !== _cachedVisibleHash;

  if (!needsRecompute) {
    return;
  }

  graphics.clear();

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    _chunkKeyTemp.x = chunkX;
    _chunkKeyTemp.y = chunkY;
    const chunk = loadedChunks.get(chunkKeyInt(_chunkKeyTemp));
    if (!chunk) {
      continue;
    }

    drawChunkCanopy({ chunk });
  }

  _cachedChunksRef = loadedChunks;
  _cachedChunksSize = loadedChunks.size;
  _cachedVisibleHash = visibleHash;
};
